import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, Printer, AlertTriangle, ShieldCheck, Sparkles, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AnalyzedPhoto {
  id: string;
  url: string | null;
  filename: string | null;
  ai_damage_detected: boolean | null;
  ai_damage_types: string[] | null;
  ai_confidence_score: number | null;
  ai_analysis_result: any;
  created_at: string;
}

interface InspectionSummary {
  damage_percentage: number;
  overall_severity: string;
  next_steps: string[];
  estimated_lifespan_years: number;
  urgent_repairs_needed: boolean;
  summary: string;
}

interface Props {
  jobId: string;
}

const severityColor: Record<string, string> = {
  low: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  moderate: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  severe: "bg-orange-500/15 text-orange-700 border-orange-500/30",
  critical: "bg-red-500/15 text-red-700 border-red-500/30",
};

export const InspectionReportPanel = ({ jobId }: Props) => {
  const [photos, setPhotos] = useState<AnalyzedPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState<InspectionSummary | null>(null);

  const loadPhotos = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("photos")
      .select("id, url, filename, ai_damage_detected, ai_damage_types, ai_confidence_score, ai_analysis_result, created_at")
      .eq("job_id", jobId)
      .eq("ai_analyzed", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      toast.error("Failed to load analyzed photos");
    } else {
      setPhotos((data as any) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPhotos();
  }, [jobId]);

  const damagedPhotos = useMemo(
    () => photos.filter((p) => p.ai_damage_detected),
    [photos]
  );

  const findings = useMemo(() => {
    return damagedPhotos.flatMap((p) => {
      const types = p.ai_damage_types || [];
      const result = p.ai_analysis_result || {};
      return types.map((t) => ({
        damage_type: t,
        severity_score:
          result.severity === "severe" ? 5 :
          result.severity === "moderate" ? 3 :
          result.severity === "minor" ? 2 : 3,
        linear_measurement_ft: 0,
        confidence_score: p.ai_confidence_score || 0,
        ai_narrative: (result.findings || []).join(" ") || `${t} detected.`,
      }));
    });
  }, [damagedPhotos]);

  const generateReport = async () => {
    if (findings.length === 0) {
      toast.error("No damage findings yet — analyze some photos first.");
      return;
    }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-inspection-summary", {
        body: {
          findings,
          totalSquares: 0,
          totalLinearFeet: 0,
        },
      });
      if (error) throw error;
      setSummary(data as InspectionSummary);
      toast.success("Inspection report generated");
    } catch (err: any) {
      console.error(err);
      toast.error("Report generation failed: " + (err?.message || "Unknown"));
    } finally {
      setGenerating(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  return (
    <Card className="border-primary/20 shadow-lg print:shadow-none print:border-0">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 border-b print:bg-white">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-primary" />
            Damage Inspection Report
          </CardTitle>
          <div className="flex gap-2 print:hidden">
            <Button size="sm" variant="outline" onClick={loadPhotos} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={generateReport} disabled={generating || damagedPhotos.length === 0}>
              {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {summary ? "Regenerate" : "Generate Report"}
            </Button>
            {summary && (
              <Button size="sm" variant="outline" onClick={printReport}>
                <Printer className="w-4 h-4 mr-1" />
                Print / PDF
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Photos analyzed" value={photos.length} />
          <Stat label="With damage" value={damagedPhotos.length} />
          <Stat label="Findings" value={findings.length} />
          <Stat
            label="Avg confidence"
            value={
              damagedPhotos.length
                ? `${Math.round(
                    (damagedPhotos.reduce((s, p) => s + (p.ai_confidence_score || 0), 0) /
                      damagedPhotos.length) * 100
                  )}%`
                : "—"
            }
          />
        </div>

        {/* Empty state */}
        {!loading && photos.length === 0 && (
          <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
            No analyzed photos yet. Upload and analyze photos in the section above to build the report.
          </div>
        )}

        {/* AI summary */}
        {summary && (
          <div className="space-y-4 rounded-lg border bg-card p-5">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className={`border ${severityColor[summary.overall_severity] || ""}`}>
                {summary.overall_severity?.toUpperCase()} SEVERITY
              </Badge>
              <Badge variant="outline">
                {summary.damage_percentage?.toFixed(1)}% roof affected
              </Badge>
              <Badge variant="outline">
                ~{summary.estimated_lifespan_years} yrs remaining
              </Badge>
              {summary.urgent_repairs_needed && (
                <Badge className="bg-red-500/15 text-red-700 border border-red-500/30">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Urgent repairs needed
                </Badge>
              )}
              {!summary.urgent_repairs_needed && (
                <Badge className="bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  No emergency action
                </Badge>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-1">Executive Summary</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{summary.summary}</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Recommended Next Steps</h3>
              <ol className="space-y-2 text-sm">
                {summary.next_steps?.map((step, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-semibold text-primary shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {/* Photo evidence */}
        {damagedPhotos.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3">Photo Evidence ({damagedPhotos.length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {damagedPhotos.map((p) => (
                <div key={p.id} className="rounded-lg border overflow-hidden bg-muted/30">
                  {p.url && (
                    <img
                      src={p.url}
                      alt={p.filename || "Damage photo"}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-2 space-y-1">
                    <div className="flex flex-wrap gap-1">
                      {(p.ai_damage_types || []).slice(0, 3).map((t, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">
                          {t.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Confidence: {Math.round((p.ai_confidence_score || 0) * 100)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value }: { label: string; value: string | number }) => (
  <div className="rounded-lg border bg-muted/30 p-3">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-2xl font-semibold mt-1">{value}</div>
  </div>
);
